/**
 * CHiATO Wishlist — single source of truth.
 *
 * localStorage-based, under 'chiato_wishlist'. Each entry is a record
 * { id: <product.id>, handle: "<product.handle>" }.
 *
 * IDENTITY IS THE ID, never the handle. A product handle is translatable, so the
 * same product is 'chiato-bloom' in English and something else under /da — keying
 * on the handle meant the heart read as un-saved after a language switch and a
 * second click stored a duplicate. product.id is the same integer in every locale.
 *
 * The handle is still stored beside it, because it is the only way to render the
 * card: Liquid can look a product up by handle (all_products) but not by id, so
 * the Section Rendering URL on the wishlist page needs one. It is a cache, not the
 * key — every syncButton() refreshes it from the DOM, so it tracks whatever locale
 * the shopper is actually browsing in.
 *
 * Any element with [data-wishlist-toggle][data-product-id][data-product-handle]
 * participates — product cards, PDP buttons and injected cards alike — via one
 * delegated listener (no per-node binding, so there is no double-toggle). Active
 * state is expressed with aria-pressed; the card CSS drives the heart icon from
 * that. Every change dispatches 'wishlist:updated' so the header badge
 * (sections/header.liquid) and the wishlist page can react.
 */
window.ChiatoWishlist = (function () {
  var KEY = 'chiato_wishlist';

  /**
   * Read + migrate. Pure: never writes. init() persists the migrated form once.
   *
   * Three historical formats have lived under this key, and an existing wishlist
   * must survive all of them — silently emptying someone's saved items is a worse
   * bug than the one this fixes:
   *   v3 (now)  { id, handle } records            → kept as-is
   *   v2        bare handle strings               → { id: null, handle } (id is
   *             backfilled by syncButton the first time that product is seen)
   *   v1        bare product-id strings, ~14 digits → dropped, as before: with no
   *             handle there is no way to render or even name the product, so it
   *             would only be an invisible entry inflating the header badge.
   */
  function get() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      var out = [];
      raw.forEach(function (e) {
        if (e && typeof e === 'object') {
          if (e.handle || e.id) out.push({ id: e.id == null ? null : Number(e.id), handle: e.handle || null });
        } else if (typeof e === 'string' && e) {
          // A short all-digit handle like "1899" is a legitimate handle; only the
          // long ones are v1 product ids.
          if (!/^\d{10,}$/.test(e)) out.push({ id: null, handle: e });
        }
      });
      return out;
    } catch (e) { return []; }
  }
  function save(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { list: list } }));
  }

  /**
   * Locate an entry. Prefer the id; fall back to the handle so entries migrated
   * from v2 (id still null) and pre-backfill lookups still resolve.
   */
  function indexOf(list, id, handle) {
    var i;
    if (id != null && id !== '') {
      for (i = 0; i < list.length; i++) if (list[i].id != null && String(list[i].id) === String(id)) return i;
    }
    if (handle) {
      for (i = 0; i < list.length; i++) if (list[i].handle === handle) return i;
    }
    return -1;
  }

  function has(id, handle) { return indexOf(get(), id, handle) !== -1; }

  function toggle(id, handle) {
    var list = get();
    var i = indexOf(list, id, handle);
    if (i === -1) {
      list.push({ id: id == null ? null : Number(id), handle: handle || null });
    } else {
      list.splice(i, 1);
    }
    save(list);
    return i === -1; // true when now wishlisted
  }
  function count() { return get().length; }

  /**
   * Fill in what the stored record is missing from a rendered card: the id for a
   * v2 entry, and a fresh handle whenever the shopper is browsing a locale the
   * record has not seen. Writes only when something actually changed, so this
   * does not fire 'wishlist:updated' on every page load.
   */
  function reconcile(id, handle) {
    if (id == null || !handle) return;
    var list = get();
    var i = indexOf(list, id, handle);
    if (i === -1) return;
    var e = list[i];
    if (e.id != null && e.handle === handle) return;
    e.id = Number(id);
    e.handle = handle;
    save(list);
  }

  function syncButton(btn) {
    var handle = btn.getAttribute('data-product-handle');
    var id = btn.getAttribute('data-product-id');
    if (!handle && !id) return;
    reconcile(id, handle);
    var wished = has(id, handle);
    var S = window.CHiATO_STRINGS || {};
    btn.setAttribute('aria-pressed', wished ? 'true' : 'false');
    btn.setAttribute('aria-label', wished ? (S.wishRemove || 'Remove from wishlist')
                                          : (S.wishSave   || 'Save to wishlist'));
  }
  function updateUI(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
  }

  function init() {
    // Persist the migration once, so the header badge (which reads localStorage
    // directly and just counts entries) agrees with what the wishlist page will
    // render. Compare the serialised form, not just the length: a v2 → v3 upgrade
    // converts every entry while keeping the count identical.
    try {
      var rawStr = localStorage.getItem(KEY) || '[]';
      var clean = get();
      if (JSON.stringify(clean) !== rawStr) save(clean);
    } catch (e) {}

    // One delegated handler covers cards, PDP buttons and dynamically injected cards.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault(); // hearts usually sit inside a product link
      var handle = btn.getAttribute('data-product-handle');
      var id = btn.getAttribute('data-product-id');
      if (!handle && !id) return;
      toggle(id, handle);
      syncButton(btn);
    });

    updateUI();
    document.addEventListener('wishlist:updated', function () { updateUI(); });

    // Keep injected cards (recently viewed, wishlist page) in sync.
    if (window.MutationObserver) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            if (node.matches && node.matches('[data-wishlist-toggle]')) syncButton(node);
            if (node.querySelectorAll) node.querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  document.addEventListener('DOMContentLoaded', init);

  return { get: get, has: has, toggle: toggle, count: count, updateUI: updateUI };
})();
